/* ============================================================
   Browser location
   ------------------------------------------------------------
   Used when someone punches in or out, and when a work from home
   request is raised.

   Nothing here ever throws or rejects. Attendance is not blocked
   on a location: a refused prompt, a browser without the API or
   a page served over plain http all resolve to `ok: false`, and
   the caller carries on without one.
   ============================================================ */

const TIMEOUT_MS = 10000;

/* a reading from the last minute is close enough for a punch */
const MAX_AGE_MS = 60000;

/* Why a location could not be had. Kept free of context so a punch can add
   "the punch was saved without it" and a form can add "it is required". */
export const LOCATION_MESSAGES = {
    unsupported: "This browser cannot share a location.",
    insecure: "Location needs a secure (https) connection.",
    denied: "Location permission was refused.",
    timeout: "Could not get your location in time.",
    unavailable: "Your location is unavailable right now.",
};

export const locationProblem = (result) =>
    LOCATION_MESSAGES[result && result.reason] || LOCATION_MESSAGES.unavailable;

export const getBrowserLocation = () => new Promise((resolve) => {
    if (!navigator.geolocation) {
        resolve({ ok: false, reason: "unsupported" });
        return;
    }

    /* the API is only handed out on a secure origin, and localhost counts */
    if (window.isSecureContext === false) {
        resolve({ ok: false, reason: "insecure" });
        return;
    }

    navigator.geolocation.getCurrentPosition(
        (position) => resolve({
            ok: true,
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
        }),
        (error) => resolve({
            ok: false,
            reason: error && error.code === 1 ? "denied"
                : error && error.code === 3 ? "timeout"
                    : "unavailable",
        }),
        { enableHighAccuracy: true, timeout: TIMEOUT_MS, maximumAge: MAX_AGE_MS },
    );
});

/* the coordinates a request should carry, or nothing at all */
export const locationBody = (result) => (result && result.ok
    ? { latitude: result.latitude, longitude: result.longitude, accuracy: result.accuracy }
    : {});

const hasCoords = (location) =>
    Boolean(location) && Number.isFinite(Number(location.latitude)) &&
    Number.isFinite(Number(location.longitude));

/* four decimals is about eleven metres — enough to place someone without
   filling a table cell with digits */
export const formatLocation = (location) => (hasCoords(location)
    ? `${Number(location.latitude).toFixed(4)}, ${Number(location.longitude).toFixed(4)}`
    : "");

export const mapLink = (location) => (hasCoords(location)
    ? `https://www.google.com/maps/search/?api=1&query=${location.latitude},${location.longitude}`
    : "");

export const locationTitle = (location, label = "Recorded") => {
    if (!hasCoords(location)) return "";
    const accuracy = Number(location.accuracy);
    return `${label} at ${formatLocation(location)}`
        + (Number.isFinite(accuracy) && accuracy > 0 ? ` (±${Math.round(accuracy)}m)` : "")
        + " — opens in Google Maps";
};

export { hasCoords };
